import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import moment from "moment"
import * as customerApis from "../../utilities/apis/customer"
import * as orderApis from "../../utilities/apis/order"
import * as dispatcherServices from "../../utilities/services/dispatcher"
import { isDeal } from "../../utilities/enumerations/constants"
import Layout from "../../components/Layout"
import NewMessageModal from "../../components/NewMessageModal"
import ShortMessagesModal from "../../components/ShortMessagesModal"
import AttachmentModal from "../../components/AttachmentModal"
import { getSrc } from "../../utilities/enumerations/constants"

import { collection, query, where, orderBy, limit, onSnapshot, addDoc, serverTimestamp, doc, updateDoc, setDoc, getDoc, writeBatch } from "firebase/firestore";
import { db } from "../../utilities/firebase/firestore"

const MESSAGES_PAGE_SIZE = 10
const UNREAD_COUNT_LIMIT = 100

// Customer messages the operator hasn't read yet (the mobile app writes them with read: false)
const unreadCustomerMessagesQuery = (customerId, max) => query(
  collection(db, "customers", customerId, "messages"),
  where("sender", "==", "customer"),
  where("read", "==", false),
  limit(max)
)

const AttachmentCard = ({ attachment, onRemove }) => {
  return (
    <div className="d-flex gap-2 align-items-center border rounded-3 bg-white p-2">
      <img src={getSrc(attachment.thumbnail_url)} className="border rounded-3" alt={attachment.title} width={48} height={48} />
      <div className="flex-grow-1">
        <div className="fw-bold">{attachment.title}</div>
        <div className="small text-muted">{attachment.type === "deal" && `${attachment.items} items · `}Rs. {attachment.price.toFixed(2)}</div>
      </div>
      {onRemove && <button type="button" className="btn-close" aria-label="Remove" onClick={onRemove}></button>}
    </div>
  )
}

// Location shared from the customer app: map preview (OpenStreetMap embed, no key) linking to Google Maps
const LocationCard = ({ location }) => {
  const { latitude, longitude } = location
  const bbox = [longitude - 0.005, latitude - 0.003, longitude + 0.005, latitude + 0.003].join(",")

  return (
    <a href={`https://www.google.com/maps?q=${latitude},${longitude}`} target="_blank" rel="noreferrer" className="d-block border rounded-3 bg-white overflow-hidden text-decoration-none text-body" style={{ width: 260 }}>
      <iframe
        title="Location"
        src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${latitude},${longitude}`}
        width="100%"
        height="140"
        className="d-block border-0"
        style={{ pointerEvents: "none" }}
        loading="lazy"
      />
      <div className="d-flex gap-2 align-items-center p-2">
        <i className="bi bi-geo-alt-fill text-danger"></i>
        <div className="flex-grow-1">
          <div className="fw-bold">Location</div>
          <div className="small text-muted">{latitude.toFixed(6)}, {longitude.toFixed(6)}</div>
        </div>
        <i className="bi bi-box-arrow-up-right text-muted"></i>
      </div>
    </a>
  )
}

const UnreadBadge =({ customerId }) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    const unsubscribe = onSnapshot(unreadCustomerMessagesQuery(customerId, UNREAD_COUNT_LIMIT), (snapshot) => {
      setCount(snapshot.size)
    })

    return () => unsubscribe()
  }, [customerId])

  if (!count) return null

  return <span className="badge rounded-pill bg-success">{count >= UNREAD_COUNT_LIMIT ? `${UNREAD_COUNT_LIMIT - 1}+` : count}</span>
}

const Chats = () => {
  const navigate = useNavigate()
  const containerRef = useRef(null)
  const contentRef = useRef(null)
  const composerRef = useRef(null)
  const scrollStateRef = useRef({ firstId: null, lastId: null, scrollHeight: 0 })
  const stickToBottomRef = useRef(true)
  const { token } = useSelector(state => state.user)
  const [customers, setCustomers] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [messages, setMessages] = useState([])
  const [messageLimit, setMessageLimit] = useState(MESSAGES_PAGE_SIZE)
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [text, setText] = useState("")
  const [attachment, setAttachment] = useState(null)
  const [photo, setPhoto] = useState(null)
  const [photoPreview, setPhotoPreview] = useState(null)
  const photoInputRef = useRef(null)

  const selectCustomer = (mobile) => {
    if (mobile === selectedCustomer) return

    scrollStateRef.current = { firstId: null, lastId: null, scrollHeight: 0 }
    stickToBottomRef.current = true
    setMessages([])
    setMessageLimit(MESSAGES_PAGE_SIZE)
    setHasMoreMessages(false)
    setLoadingMessages(true)
    setSelectedCustomer(mobile)
  }

  const loadOlderMessages = () => {
    if (!hasMoreMessages || loadingMessages) return

    setLoadingMessages(true)
    setMessageLimit(prev => prev + MESSAGES_PAGE_SIZE)
  }

  const handleMessagesScroll = (e) => {
    const container = e.target
    stickToBottomRef.current = container.scrollHeight - container.scrollTop - container.clientHeight < 40

    if (container.scrollTop <= 20) loadOlderMessages()
  }

  const sendMessage = async (payload) => {
    try {
      await customerApis.sendMessage(token, payload)
    } catch (error) {

    }
  }

  const handleSend = async () => {
    const message = text
    const attached = attachment
    const file = photo
    if (!selectedCustomer || (!message.trim() && !attached && !file)) return

    // Clear first so a quick second Enter/click can't send the same text twice
    setText("")
    setAttachment(null)
    setPhoto(null)

    // Push notification text: the message followed by the deal/product details
    const details = attached && [
      attached.title,
      attached.type === "deal" && `${attached.items} items`,
      `Rs. ${attached.price.toFixed(2)}`,
    ].filter(Boolean).join(" - ")
    const notification = [message.trim(), details].filter(Boolean).join("\n")

    // Chat list preview: the message, the item title, or "Photo" for photo-only messages
    const preview = message.trim() ? message : attached ? attached.title : "Photo"

    // The backend stores the file and sends the push in one call, and returns the link to show
    let imageUrl = null
    if (file) {
      try {
        const formData = new FormData()
        formData.append("mobile", selectedCustomer)
        formData.append("message", notification || "Photo")
        formData.append("file", file)

        const response = await customerApis.sendMessageFile(token, formData)
        if (!response.status) throw new Error(response.message)

        imageUrl = response.url
      } catch (error) {
        alert(error.message)
        setText(message)
        setAttachment(attached)
        setPhoto(file)
        return
      }
    }

    const messageRef = collection(db, "customers", selectedCustomer, "messages")
    await addDoc(messageRef, {
      text: message,
      ...(attached && { attachment: attached }),
      ...(imageUrl && { image_url: imageUrl }),
      sender: "support",
      read: false,
      createdAt: serverTimestamp(),
    })

    const customerRef = doc(db, "customers", selectedCustomer)
    await updateDoc(customerRef, {
      lastMessage: preview,
      lastUpdated: serverTimestamp(),
    })

    if (!file) {
      await sendMessage({
        mobile: selectedCustomer,
        message: notification
      })
    }
  }

  const handlePhotoChange = (e) => {
    const file = e.target.files[0]
    e.target.value = ""
    if (!file) return

    setPhoto(file)
    composerRef.current.focus()
  }

  const handleComposerKeyDown = (e) => {
    // Enter sends, Shift+Enter adds a new line (skip while an IME is composing)
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleShortMessageSelect = (message) => {
    setText(message)
    composerRef.current.focus()
  }

  const handleAttachmentSelect = (item) => {
    setAttachment(item)
    composerRef.current.focus()
  }

  const handleSelect = async (customer) => {
    const customerRef = doc(db, "customers", customer.mobile)
    const customerSnap = await getDoc(customerRef)

    if (!customerSnap.exists()) {
      await setDoc(customerRef, {
        name: customer.name,
        lastMessage: "",
        lastUpdated: null,
      })
    }
    selectCustomer(customer.mobile)
  }

  useEffect(() => {
    if (!photo) {
      setPhotoPreview(null)
      return
    }

    const url = URL.createObjectURL(photo)
    setPhotoPreview(url)

    return () => URL.revokeObjectURL(url)
  }, [photo])

  useEffect(() => {
    const q = query(collection(db, "customers"), orderBy("lastUpdated", "desc"))

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      setCustomers(data)
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    if (!selectedCustomer) return

    // While a chat is open, mark every unread customer message in it as read (500 is the batch write cap)
    const unsubscribe = onSnapshot(unreadCustomerMessagesQuery(selectedCustomer, 500), (snapshot) => {
      if (snapshot.empty) return

      const batch = writeBatch(db)
      snapshot.docs.forEach((doc) => batch.update(doc.ref, { read: true }))
      batch.commit()
    })

    return () => unsubscribe()
  }, [selectedCustomer])

  useEffect(() => {
    if (!selectedCustomer) return

    // Newest messages first so the limit keeps the latest ones, then reversed for display
    const q = query(collection(db, "customers", selectedCustomer, "messages"), orderBy("createdAt", "desc"), limit(messageLimit))

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })).reverse()
      setMessages(data)

      // A partial cache result can arrive before the server result, so wait for a full page or server data
      if (!snapshot.metadata.fromCache || snapshot.size >= messageLimit) {
        setHasMoreMessages(snapshot.size >= messageLimit)
        setLoadingMessages(false)
      }
    })

    return () => unsubscribe()
  }, [selectedCustomer, messageLimit])

  useLayoutEffect(() => {
    const container = containerRef.current
    const prev = scrollStateRef.current
    const firstId = messages.length ? messages[0].id : null
    const lastId = messages.length ? messages[messages.length - 1].id : null

    if (!prev.lastId || lastId !== prev.lastId) {
      // First load or a new message arrived: stick to the bottom
      stickToBottomRef.current = true
      container.scrollTop = container.scrollHeight
    } else if (firstId !== prev.firstId) {
      // Older messages were prepended: keep the view where the user was
      container.scrollTop += container.scrollHeight - prev.scrollHeight
    }

    scrollStateRef.current = { firstId, lastId, scrollHeight: container.scrollHeight }
  }, [messages])

  useLayoutEffect(() => {
    // Grow the composer with its content, up to the CSS max-height
    const textarea = composerRef.current
    textarea.style.height = "auto"
    textarea.style.height = `${textarea.scrollHeight}px`
  }, [text])

  useEffect(() => {
    // Content can grow after render (icons/fonts loading), so stay pinned to the bottom unless the user scrolled up
    const observer = new ResizeObserver(() => {
      const container = containerRef.current
      if (stickToBottomRef.current) container.scrollTop = container.scrollHeight
    })
    observer.observe(contentRef.current)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    // If the loaded messages don't fill the pane there's nothing to scroll, so keep loading
    const container = containerRef.current
    if (hasMoreMessages && !loadingMessages && container.scrollHeight <= container.clientHeight) {
      setLoadingMessages(true)
      setMessageLimit(prev => prev + MESSAGES_PAGE_SIZE)
    }
  }, [messages, hasMoreMessages, loadingMessages])

  return (
    <Layout
      header={true}
      footer={true}
    >
      <NewMessageModal handleSelect={handleSelect} />
      <ShortMessagesModal handleSelect={handleShortMessageSelect} />
      <AttachmentModal handleSelect={handleAttachmentSelect} />

      <main id="order" className="flex-column">
        <div className="d-flex gap-2 align-items-center p-2">
          <h3 className="fw-bold m-0">Chats</h3>
          <button type="button" className="btn btn-sm btn-light-theme ms-auto" data-bs-toggle="modal" data-bs-target="#newMessageModal">Compose New Chat</button>
        </div>

        <div className="flex-grow-1 d-flex overflow-y-auto border-top">
          <aside className="w-25 border-end">
            <div className="list-group shadow-none">
              {customers.map((customer) => {
                return (
                  <a onClick={() => selectCustomer(customer.id)} key={customer.id} className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center gap-2 border-0 border-bottom rounded-0 ${customer.id === selectedCustomer ? "active" : ""}`}>
                    <div>{customer.name?.trim() || "Customer"}<br /><span className="small">{customer.id}</span></div>
                    {customer.id !== selectedCustomer && <UnreadBadge customerId={customer.id} />}
                  </a>
                )
              })}
            </div>
          </aside>
          <section className="w-75 border-start d-flex flex-column">
            <div ref={containerRef} id="messages" className="flex-grow-1 overflow-y-auto px-2" onScroll={handleMessagesScroll}>
              <div ref={contentRef} className="pb-3">
                {loadingMessages && messages.length > 0 && <p className="text-center small text-muted my-2">Loading...</p>}
                {messages.map((message) => {
                  return (
                    <div key={message.id} className={`${message.sender} ${message.sender === "customer" ? "bg-gray-200 text-gray-700" : "bg-theme-light text-theme"} text-left p-2 rounded my-2`}>
                      {message.image_url && <img src={message.image_url} className="img-fluid rounded mb-1" style={{ maxHeight: 240 }} alt="Photo" />}
                      {message.attachment && <div className="mb-1 text-body"><AttachmentCard attachment={message.attachment} /></div>}
                      {message.location && <div className="mb-1"><LocationCard location={message.location} /></div>}
                      {message.text && <p className="message-text">{message.text.replace(/\\n/g, "\n")}</p>}
                      <p className="m-0 small text-end">
                        {message.createdAt && moment(message.createdAt.seconds * 1000).format("HH:mm")}
                        {message.sender === "support" && (
                          !message.createdAt
                            ? <span key="sending" className="ms-1 opacity-75" title="Sending"><i className="fas fa-clock"></i></span>
                            : message.read
                              ? <span key="read" className="ms-1" title="Read"><i className="fas fa-check-double"></i></span>
                              : <span key="sent" className="ms-1 opacity-75" title="Sent"><i className="fas fa-check"></i></span>
                        )}
                      </p>
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="border-top p-2">
              <div id="composer" className="border rounded-4 bg-white p-2">
                <input ref={photoInputRef} type="file" accept="image/*" className="d-none" onChange={handlePhotoChange} />
                {photoPreview && (
                  <div className="d-flex gap-2 align-items-start mb-2">
                    <img src={photoPreview} className="border rounded-3" alt="Selected" style={{ maxHeight: 96, maxWidth: 160 }} />
                    <button type="button" className="btn-close" aria-label="Remove" onClick={() => setPhoto(null)}></button>
                  </div>
                )}
                {attachment && <div className="mb-2"><AttachmentCard attachment={attachment} onRemove={() => setAttachment(null)} /></div>}
                <textarea ref={composerRef} className="form-control border-0 p-1" placeholder="Type a message" rows={1} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={handleComposerKeyDown}></textarea>
                <div className="d-flex justify-content-between align-items-center mt-1">
                  <div className="dropup">
                    <button type="button" className="btn btn-sm btn-light rounded-circle composer-icon-btn" data-bs-toggle="dropdown" aria-expanded="false" title="Add"><i className="bi bi-plus-lg"></i></button>
                    <ul className="dropdown-menu">
                      <li><button type="button" className="dropdown-item" data-bs-toggle="modal" data-bs-target="#shortMessagesModal"><i className="bi bi-slash-square me-2"></i>Shortcut Messages</button></li>
                      <li><button type="button" className="dropdown-item" data-bs-toggle="modal" data-bs-target="#attachmentModal"><i className="bi bi-box-seam me-2"></i>Product / Deal Attachment</button></li>
                      <li><button type="button" className="dropdown-item" onClick={() => photoInputRef.current.click()}><i className="bi bi-image me-2"></i>Photos</button></li>
                    </ul>
                  </div>
                  {(text.trim() || attachment || photo) && <button type="button" className="btn btn-sm btn-theme rounded-circle composer-icon-btn" onClick={handleSend} title="Send"><i className="bi bi-arrow-up"></i></button>}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </Layout>
  )
}

export default Chats
